import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import zh from './zh.json'

export type AppLocale = 'zh' | 'en'
export type MessageKey = keyof typeof zh

export const APP_LOCALE_STORAGE_KEY = 'xr.locale'

const resources = {
  zh: { common: zh },
  en: { common: en },
} as const

function normalizeLocale(value: string | null | undefined): AppLocale | null {
  if (!value) return null
  const lower = value.toLowerCase()
  if (lower.startsWith('en')) return 'en'
  if (lower.startsWith('zh')) return 'zh'
  return null
}

export function detectInitialLocale(): AppLocale {
  if (typeof window !== 'undefined') {
    try {
      const stored = normalizeLocale(window.localStorage?.getItem(APP_LOCALE_STORAGE_KEY))
      if (stored) return stored
    } catch {
      // Treat localStorage as unset when blocked by the runtime.
    }
  }

  if (typeof navigator !== 'undefined') {
    const preferred =
      normalizeLocale(navigator.language) ??
      navigator.languages
        ?.map(normalizeLocale)
        .find((locale): locale is AppLocale => Boolean(locale))
    if (preferred) return preferred
  }

  return 'zh'
}

function htmlLang(locale: string | undefined): string {
  return normalizeLocale(locale) === 'en' ? 'en' : 'zh-CN'
}

function applyDocumentLanguage(locale: string | undefined) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('lang', htmlLang(locale))
}

if (!i18next.isInitialized) {
  void i18next.use(initReactI18next).init({
    resources,
    lng: detectInitialLocale(),
    fallbackLng: 'zh',
    supportedLngs: ['zh', 'en'],
    ns: ['common'],
    defaultNS: 'common',
    keySeparator: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    initAsync: false,
  })
} else {
  void i18next.changeLanguage(detectInitialLocale())
}

applyDocumentLanguage(i18next.language)
i18next.on('languageChanged', applyDocumentLanguage)

// HMR support: when en.json / zh.json change in dev, replace the resource
// bundles in-place so newly added keys appear without a full page reload.
// Without this, i18next keeps the snapshot taken at init() and missing
// keys render as their key literal (e.g. "meTabProfile").
if (import.meta.hot) {
  import.meta.hot.accept('./en.json', (mod) => {
    if (mod) i18next.addResourceBundle('en', 'common', mod.default, true, true)
  })
  import.meta.hot.accept('./zh.json', (mod) => {
    if (mod) i18next.addResourceBundle('zh', 'common', mod.default, true, true)
  })
}

export async function setAppLocale(locale: AppLocale): Promise<void> {
  await i18next.changeLanguage(locale)
  try {
    window.localStorage?.setItem(APP_LOCALE_STORAGE_KEY, locale)
  } catch {
    // best-effort persistence
  }
  applyDocumentLanguage(locale)
}

export { i18next }
